import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const provider = params.get("provider") || "stripe";
  const isTopup = params.get("kind") === "topup";
  const providerLabel = provider.charAt(0).toUpperCase() + provider.slice(1);
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("polling");
  const attempts = useRef(0);

  useEffect(() => {
    if (!session_id) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const res = await api.get(`/subscriptions/status/${session_id}`);
        if (res.data.payment_status === "paid") {
          setStatus("success");
          await checkAuth();
          return;
        }
        if (res.data.status === "expired") {
          setStatus("expired"); return;
        }
        if (attempts.current >= 8) { setStatus("timeout"); return; }
        timer = setTimeout(poll, 2000);
      } catch {
        if (attempts.current >= 5) { setStatus("error"); return; }
        timer = setTimeout(poll, 2500);
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [session_id]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-6">
      <div className="max-w-md w-full rounded-2xl border border-line bg-bg-surface p-10 text-center" data-testid="payment-result">
        {status === "polling" && (
          <>
            <Loader2 className="h-12 w-12 mx-auto text-brand-amber animate-spin mb-4" />
            <div className="font-display text-2xl font-bold mb-2">Confirming payment...</div>
            <div className="text-sm text-ink-secondary">Hang tight — checking with {providerLabel}.</div>
          </>
        )}
        {status === "success" && (
          <>
            <div className="h-14 w-14 mx-auto rounded-full bg-brand-amber/20 flex items-center justify-center mb-4">
              <Check className="h-7 w-7 text-brand-amber" />
            </div>
            <div className="font-display text-3xl font-black tracking-tighter mb-2">{isTopup ? "Credits added." : "You're live."}</div>
            <div className="text-sm text-ink-secondary mb-6">
              {isTopup ? "Your top-up is complete — credits are in your wallet." : "Your subscription is active. Back to the control room."}
            </div>
            <button onClick={() => navigate(isTopup ? "/dashboard/billing?tab=credits" : "/dashboard")} data-testid="payment-success-back" className="rounded-md bg-brand-amber px-6 py-2.5 font-semibold text-black amber-glow">
              {isTopup ? "View wallet" : "Open dashboard"}
            </button>
          </>
        )}
        {(status === "expired" || status === "error" || status === "timeout") && (
          <>
            <div className="h-14 w-14 mx-auto rounded-full bg-brand-onair/20 flex items-center justify-center mb-4">
              <X className="h-7 w-7 text-brand-onair" />
            </div>
            <div className="font-display text-2xl font-bold mb-2">Payment didn't complete</div>
            <div className="text-sm text-ink-secondary mb-6">No charge was made. Try again from the billing page.</div>
            <button onClick={() => navigate("/dashboard/billing?tab=plan")} className="rounded-md border border-line bg-bg-elevated px-6 py-2.5 font-semibold">
              Back to billing
            </button>
          </>
        )}
      </div>
    </div>
  );
}
